import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiArrowLeftLine, RiTimeLine, RiCheckboxCircleLine,
  RiArrowRightLine, RiHistoryLine,
} from 'react-icons/ri';
import { getUserTestHistory } from '../../api/practiceTests.api';
import { C, LEVEL_COLORS } from '../../data/constants';
import { LoadingScreen, EmptyState } from '../../components/shared/UI';

const scoreColor = pct => (pct >= 70 ? C.green : pct >= 50 ? C.yellow : C.red);

export default function PracticeTestHistory() {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    getUserTestHistory()
      .then(res => { if (alive) setAttempts(res.data || []); })
      .catch(err => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const best = attempts.length ? Math.max(...attempts.map(a => a.totalScore || 0)) : 0;

  return (
    <div className="page-container ptest-page anim-fade-up">
      <div className="ptest-head">
        <div className="ptest-head-copy">
          <button className="ptest-back" onClick={() => navigate('/practice-tests')}>
            <RiArrowLeftLine size={14} /> Practice tests
          </button>
          <h1>Test History</h1>
          <p>
            {attempts.length
              ? `${attempts.length} attempt${attempts.length === 1 ? '' : 's'} · best score ${best}%`
              : 'Every mock exam you finish shows up here.'}
          </p>
        </div>
      </div>

      {error && <div className="ptest-error">{error}</div>}

      {loading ? (
        <LoadingScreen label="Loading your history" />
      ) : attempts.length === 0 ? (
        <div className="ks-card">
          <EmptyState
            icon={RiHistoryLine}
            title="No attempts yet"
            sub="Finish a practice test and your score breakdown will appear here."
          />
        </div>
      ) : (
        <div className="ptest-grid">
          {attempts.map(attempt => {
            const pct = attempt.totalScore ?? 0;
            const color = scoreColor(pct);
            const date = new Date(attempt.createdAt).toLocaleDateString(undefined, {
              day: 'numeric', month: 'short', year: 'numeric',
            });
            return (
              <article key={attempt._id} className="ks-card ptest-card" style={{ '--lvl-color': LEVEL_COLORS[attempt.level] || C.accent }}>
                <header className="ptest-card-head">
                  <span className="ptest-card-score" style={{ '--score-color': color }}>
                    {pct}<i>%</i>
                  </span>
                  <div className="ptest-card-title">
                    <h2>{attempt.testTitle || attempt.practiceTest?.title || 'Practice test'}</h2>
                    <p>{attempt.level} · {date}</p>
                  </div>
                </header>

                <div className="ptest-card-stats">
                  <span><RiCheckboxCircleLine size={14} /> {attempt.totalCorrect} / {attempt.totalQuestions} correct</span>
                  <span><RiTimeLine size={14} /> {Math.max(1, Math.round((attempt.totalDuration || 0) / 60))} min</span>
                </div>

                <div className="ptest-score-track">
                  <div className="ptest-score-fill" style={{ width: `${pct}%`, background: color }} />
                </div>

                <button
                  className="btn-ghost ptest-start"
                  onClick={() => navigate(`/practice-tests/results/${attempt._id}`)}
                >
                  View breakdown <RiArrowRightLine size={15} />
                </button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
